import React, { useEffect } from "react";
import { AiOutlineClose } from "react-icons/ai";

const CustomDetailModal = ({
  isOpen,
  onClose,
  title,
  filterComponent, // Custom React node for filters (e.g. date range, dropdowns)
  columns = [], // Array of column objects: { key, label, align?: "left" | "center" | "right", isTotal }
  data = [], // Array of row objects matching the column keys
  onRowClick, // Function: (row, rowIndex) => void
  onValueClick, // Function: (row, columnKey, value) => void
  modalWidth = "65vw",
  loading = false,
  tableWrapperClassName = "min-w-[800px]",
}) => {
  // Close on Escape key press
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      window.addEventListener("keydown", handleEsc);
    }
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  // Prevent background scrolling when modal is open. Hiding the scrollbar
  // changes the page width, which makes responsive charts behind the modal
  // resize/repaint — pad the body by the scrollbar width to keep layout stable.
  useEffect(() => {
    if (!isOpen) return;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = "unset";
      document.body.style.paddingRight = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  /** Center by default; only explicit `align: "right"` uses right alignment. */
  const alignClass = (col) => (col?.align === "right" ? "text-right" : "text-center");

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose} // Clicking on overlay closes the modal
    >
      {/* Modal Container */}
      <div
        className="relative bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col w-full max-h-[90vh] animate-in fade-in zoom-in duration-200 mt-4"
        style={{ width: modalWidth }}
        onClick={(e) => e.stopPropagation()} // Prevent clicks inside modal from closing it
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-[1px] -right-[1px] p-2 rounded-bl-xl rounded-tr-xl z-20 text-gray-900 hover:bg-gray-100 transition-colors"
          aria-label="Close modal"
        >
          <AiOutlineClose size={18} className="text-gray-900" />
        </button>

        {/* Header Area */}
        <div className="flex flex-wrap items-center justify-between gap-4 p-6 pb-4 mt-2">
          <h2 className="font-['Inter',_sans-serif] text-[22px] font-bold text-[#22C55E]">
            {title}
          </h2>
          
          {/* Optional Filters Area (e.g. Date Range pill) */}
          {filterComponent && (
            <div className="flex items-center gap-2 pr-2">
              {filterComponent}
            </div>
          )}
        </div>

        {/* Table Content Area */}
        <div className="flex-1 overflow-auto px-6 pb-6 pt-0">
          <div className={tableWrapperClassName}>
            <table className="w-full border-collapse text-center font-['Inter',_sans-serif]">
              <thead>
                <tr>
                  <th className="whitespace-nowrap pb-4 px-4 text-[14px] font-bold text-gray-900 text-center">
                    S.No.
                  </th>
                  {columns.map((col, idx) => (
                    <th
                      key={col.key || idx}
                      className={`whitespace-nowrap pb-4 px-4 text-[14px] font-bold text-gray-900 ${alignClass(col)}`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="py-12 text-center">
                      <div className="flex justify-center items-center gap-3">
                        <div className="w-6 h-6 border-4 border-[#22C55E] border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm font-medium text-gray-500 font-['Inter',_sans-serif]">Loading data...</span>
                      </div>
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length + 1}
                      className="py-12 text-center text-sm text-gray-500 font-['Inter',_sans-serif]"
                    >
                      No data available
                    </td>
                  </tr>
                ) : (
                  data.map((row, rowIndex) => (
                    <tr
                      key={row.id || rowIndex}
                      onClick={() => onRowClick && onRowClick(row, rowIndex)}
                      className={`transition-colors ${
                        rowIndex % 2 === 1 ? "bg-[#F9FAFB]" : "bg-white"
                      } ${onRowClick ? "cursor-pointer hover:bg-gray-100" : ""}`}
                    >
                      <td className="whitespace-nowrap px-4 py-4 text-[13px] font-medium text-gray-500 text-center">
                        {rowIndex + 1}
                      </td>
                      {columns.map((col, colIndex) => {
                        const value = row[col.key];
                        // Highlight Total column or explicitly marked total columns
                        const isTotal = col.isTotal || col.key.toLowerCase() === "total";
                        
                        return (
                          <td
                            key={col.key || colIndex}
                            onClick={(e) => {
                              if (onValueClick) {
                                e.stopPropagation(); // Don't trigger row click if clicking a specific value
                                onValueClick(row, col.key, value);
                              }
                            }}
                            className={`whitespace-nowrap px-4 py-4 text-[13px] ${alignClass(col)} ${
                              isTotal
                                ? "font-bold text-gray-900"
                                : "font-medium text-gray-500"
                            } ${
                              onValueClick
                                ? "cursor-pointer hover:text-blue-600 hover:underline"
                                : ""
                            }`}
                          >
                            {value !== undefined && value !== null ? value : "-"}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomDetailModal;
